<template>
  <div class="settings--content inbox-quality-settings">
    <div v-if="uiFlags.isFetchingBusinessManager" class="inbox-quality-settings__loading">
      <spinner size="small" :colorScheme="'dark'"/>
      <span>{{ $t('INBOX_MGMT.BUSINESS_MANAGER.LOADING') }}</span>
    </div>
    <span class="inbox-quality-settings__last-updated" v-if="hasData">{{ lastUpdated }}</span>
    <div class="inbox-quality-settings__box" v-if="hasData">
      <div class="inbox-quality-settings__column">
        <div class="inbox-quality-settings__item">
          <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.WHATSAPP_DISPLAY_NAME') }}:</span>
          <span class="inbox-quality-settings__value">{{ whatsappName }}</span>
        </div>
        <div class="inbox-quality-settings__item">
          <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.NUMBER_QUALITY') }}:</span>
          <span class="inbox-quality-settings__value">{{ qualityRatingDisplay }}</span>
        </div>
        <div class="inbox-quality-settings__item">
          <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.BM_VERIFICATION') }}:</span>
          <span class="inbox-quality-settings__value">{{ bmVerificationDisplay }}</span>
        </div>
        <div class="inbox-quality-settings__item">
          <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.WABA_STATUS') }}:</span>
          <span class="inbox-quality-settings__value">{{ wabaStatusDisplay }}</span>
        </div>
        <div class="inbox-quality-settings__item">
          <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.TEMPLATE_SEND_LIMIT') }}:</span>
          <span class="inbox-quality-settings__value">{{ currentLimitDisplay }}</span>
        </div>
      </div>
      <div class="inbox-quality-settings__column">
        <div class="inbox-quality-settings__item">
          <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.GREEN_BADGE') }}:</span>
          <span class="inbox-quality-settings__value">{{ greenBadgeDisplay }}</span>
        </div>
        <div class="inbox-quality-settings__item">
          <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.HOMOLOGATION_RESPONSIBLE') }}:</span>
          <span class="inbox-quality-settings__value">{{ homologationResponsible }}</span>
        </div>
        <div class="inbox-quality-settings__item has-copy">
          <div class="inbox-quality-settings__value-row">
            <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.BUSINESS_MANAGER_ID') }}:</span>
            <span class="inbox-quality-settings__value">{{ businessManagerId }}</span>
          </div>
          <woot-button
            type="submit"
            variant="clear"
            size="large"
            color-scheme="secondary"
            icon="clipboard"
            class-names="copy-icon"
            @click="onCopy(businessManagerId)"
          />
        </div>
        <div class="inbox-quality-settings__item has-copy">
          <div class="inbox-quality-settings__value-row">
            <span class="inbox-quality-settings__label">{{ $t('INBOX_MGMT.BUSINESS_MANAGER.WABA_ID') }}:</span>
            <span class="inbox-quality-settings__value">{{ wabaId }}</span>
          </div>
          <woot-button
            type="submit"
            variant="clear"
            size="large"
            color-scheme="secondary"
            icon="clipboard"
            class-names="copy-icon"
            @click="onCopy(wabaId)"
          />
        </div>
      </div>
    </div>
    <div v-if="!hasData && !uiFlags.isFetchingBusinessManager" class="inbox-quality-settings__empty">
      <img
        class="inbox-quality-settings__illustration"
        src="~dashboard/assets/images/business_manager_empty.svg"
      />
      <p>
        {{ $t('INBOX_MGMT.BUSINESS_MANAGER.EMPTY_MESSAGE') }}
      </p>
    </div>
    <business-manager-disclaimer />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner';
import alertMixin from 'shared/mixins/alertMixin';
import BusinessManagerDisclaimer from './BusinessManagerDisclaimer';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export default {
  name: 'BusinessManagerSettings',
  components: {
    Spinner,
    BusinessManagerDisclaimer,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      hasData: false,
      rawData: null,
    };
  },
  watch: {
    'inbox.id': {
      immediate: false,
      handler() {
        this.resetState();
        this.fetchBusinessManager();
      },
    },
  },
  mounted() {
    this.fetchBusinessManager();
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    notAvailable() {
      return this.$t('INBOX_MGMT.BUSINESS_MANAGER.NOT_AVAILABLE');
    },
    whatsappName() {
      return this.rawData?.whatsapp_name || this.notAvailable;
    },
    qualityRatingDisplay() {
      return this.translateWithIndicator(
        this.rawData?.quality_rating,
        'QUALITY_RATING',
        { HIGH: '🟢', MEDIUM: '🟡', LOW: '🔴' }
      );
    },
    bmVerificationDisplay() {
      return this.translateWithIndicator(
        this.rawData?.fb_account_status,
        'FB_ACCOUNT_STATUS',
        { VERIFIED: '🟢', NOT_VERIFIED: '🔴', PENDING: '🟡' }
      );
    },
    wabaStatusDisplay() {
      return this.translateWithIndicator(
        this.rawData?.status,
        'STATUS',
        { APPROVED: '🟢', PENDING: '🟡', REJECTED: '🔴', BANNED: '🔴' }
      );
    },
    currentLimitDisplay() {
      const val = this.rawData?.current_limit?.toUpperCase();
      if (!val) return this.notAvailable;
      const key = `INBOX_MGMT.BUSINESS_MANAGER.CURRENT_LIMIT.${val}`;
      return this.$te(key) ? this.$t(key) : val;
    },
    greenBadgeDisplay() {
      const isOba = this.rawData?.is_oba;
      if (isOba === null || isOba === undefined) return this.notAvailable;
      return isOba
        ? `🟢 ${this.$t('INBOX_MGMT.BUSINESS_MANAGER.YES')}`
        : `🔴 ${this.$t('INBOX_MGMT.BUSINESS_MANAGER.NO')}`;
    },
    homologationResponsible() {
      return this.rawData?.homologation_responsible || this.notAvailable;
    },
    businessManagerId() {
      return this.rawData?.fb_business_id || this.notAvailable;
    },
    wabaId() {
      return this.rawData?.external_id || this.notAvailable;
    },
    lastUpdated() {
      const date = this.rawData?.last_updated;
      if (!date) return this.notAvailable;
      const locale = (this.$i18n?.locale || 'pt_BR').replace('_', '-');
      const dateFormatted = new Date(date).toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      return this.$t('INBOX_MGMT.BUSINESS_MANAGER.LAST_UPDATED', { date: dateFormatted });
    },
  },
  methods: {
    resetState() {
      this.hasData = false;
      this.rawData = null;
    },
    translateWithIndicator(value, dictKey, colorMap) {
      if (!value) return this.notAvailable;
      const upper = value.toUpperCase();
      const emoji = colorMap[upper] || '';
      const i18nKey = `INBOX_MGMT.BUSINESS_MANAGER.${dictKey}.${upper}`;
      const label = this.$te(i18nKey) ? this.$t(i18nKey) : value;
      return emoji ? `${emoji} ${label}` : label;
    },
    async fetchBusinessManager() {
      try {
        const data = await this.$store.dispatch('inboxes/getBusinessManager', {
          inboxId: this.inbox.id,
        });
        this.hasData = Boolean(data?.has_data);
        this.rawData = this.hasData ? data : null;
      } catch (error) {
        this.resetState();
        this.showAlert(error?.message || this.$t('INBOX_MGMT.BUSINESS_MANAGER.ERROR_FETCH'));
      }
    },
    async onCopy(value) {
      await copyTextToClipboard(value);
      this.showAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/variables';

.inbox-quality-settings__box{
  border-radius: 10px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 2rem;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-normal;
}

.inbox-quality-settings__column {
  display: flex;
  flex-direction: column;
  gap: $space-small;
}

.inbox-quality-settings__item {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5edf6;
  margin: 0;

  &.has-copy {
    flex-direction: row;
    justify-content: space-between;
  }
}

.inbox-quality-settings__label {
  font-size: var(--font-size-small);
  color: var(--s-600);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
}

.inbox-quality-settings__value-row {
  display: flex;
  gap: $space-smaller;
  flex-direction: column;
}

.inbox-quality-settings__value {
  font-size: var(--font-size-medium);
  color: var(--s-900);
  font-weight: var(--font-weight-bold);
  line-height: 1.45;
  word-break: break-word;
  flex: 1;
}

.inbox-quality-settings__loading {
  display: flex;
  align-items: center;
  gap: $space-small;
  padding: $space-large 0;
}

.inbox-quality-settings__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: $space-large $space-normal;
}

.inbox-quality-settings__illustration {
  max-width: 280px;
  width: 100%;
  margin-bottom: $space-normal;
}

.inbox-quality-settings__last-updated {
  align-self: start;
  margin: $space-small;
  font-weight: bold;
}

@media screen and (max-width: 768px) {
  .inbox-quality-settings__box {
    grid-template-columns: 1fr;
    padding: $space-normal;
    gap: $space-small;
  }

  .inbox-quality-settings__last-updated {
    margin: $space-small 0;
  }
}
</style>